/**
 * @author Ketan Shetye
 * @created 2025-10-09
 * @description applicant home component file
 */

import {
  Component,
  OnInit,
  AfterViewInit,
  TemplateRef,
  ElementRef,
  ViewChild,
  HostListener
} from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import {  distinctUntilChanged, filter, finalize, map, take, takeUntil } from 'rxjs/operators';
import { MatMenuTrigger } from '@angular/material/menu';

import { Images, svgs } from 'src/assets/export.assets';
import {
  ApplicantHomeData,
  ProfileMenuData
} from './applicant-home.component.model';
import { applicantHomeRoutesNames } from 'src/app/services/mock-data/applicant-home-constants/applicant-home-constants';
import { DataService, SecurityService, WorkflowContextService } from 'src/app/services';
import { Client } from 'src/app/models';
import { CommonConfirmationModalData } from 'src/app/components/common/common-confirmation-modal/common-confirmation-modal.component.model';
import { MyApplicationsService } from '../my-applications/my-applications.service';

@Component({
  selector: 'wm-app-applicant-home',
  standalone: false,
  templateUrl: './applicant-home.component.html',
  styleUrls: ['./applicant-home.component.scss']
})
export class ApplicantHomeComponent implements OnInit, AfterViewInit {
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    public context: WorkflowContextService,
    private dataService: DataService,
    private _router: Router,
    private _securitySvc: SecurityService,
    private myApplicationsService:MyApplicationsService

  ) {
    this.clientId = this.route.parent.snapshot.paramMap.get('clientId');
  }

  applicantHomeAssets = {
    profilePic: Images.dummyProfilePic,
    seal: this.context?.client?.logoURL,
    keyBoardDropDownIcon: svgs.keyboardArrowDownIcon.name,
    hamburgerIcon: svgs.hamburgerIcon.name,
    placeholderLogo : Images.geoPermitsLogoWhiteBg
  };

  applicantHomeDetails: ApplicantHomeData = {
    primaryDrawerItems: [],
    profileMenu: {
      isProfileMenuOpen: false
    }
  };

  hasAlerts: boolean = false; //code added by krutika

  // sidebar visibility flag
  hideSidebar = false;

  // profile menu state & refs
  isProfileMenuOpen = false;
  @ViewChild('profileMenu', { read: ElementRef }) profileMenuRef?: ElementRef;
  @ViewChild('profileBtnWrap', { read: ElementRef }) profileBtnWrapRef?: ElementRef;
  // Added by amisha on 2026-05-11.
  // Reason: close the Material overlay menu after navigation/sign-out actions and adjust its layer on mobile.
  @ViewChild('profileMenuTrigger', { read: MatMenuTrigger }) profileMenuTrigger?: MatMenuTrigger;
  @ViewChild('primaryDrawerMenu') primaryDrawerMenu?: ElementRef<HTMLElement>;
  canScrollMenuLeft = false;
  canScrollMenuRight = false;
  selectedDrawerIndex: number | null = null;
  isNavigatingFromWorkflow = false;
  private readonly elevatedApplicantMenuSelectors = [
    '.applicant-profile-menu-panel',
    '.jurisdiction-menu-panel'
  ];

  // Start code added by Sanchit on 03/17/2026
  isLogoLoading = true;

  onLogoLoad() {
    this.isLogoLoading = false;
  }

  onLogoError(event: Event) {
    const img = event.target as HTMLImageElement;
    img.src = this.applicantHomeAssets.placeholderLogo;
    this.isLogoLoading = false;
  }
  // End code added by Sanchit on 03/17/2026

  ngOnInit(): void {

    this.context.user$.subscribe(user => {
         if (user) {
            this.applicantHomeDetails.primaryDrawerItems = [
              {
                laptopDisplay: applicantHomeRoutesNames.applyForPermit.laptopDisplay,
                mobileDisplay: applicantHomeRoutesNames.applyForPermit.mobileDisplay,
                navigateTo: applicantHomeRoutesNames.applyForPermit.route
              },
              {
                laptopDisplay: applicantHomeRoutesNames.myApplications.laptopDisplay,
                mobileDisplay: applicantHomeRoutesNames.myApplications.mobileDisplay,
                navigateTo: applicantHomeRoutesNames.myApplications.route
              },
               // Added by Amisha on 05/21/2026.
              // Reason: add the applicant-side My Renewals drawer entry for the Janus navigation flow.
              {
                laptopDisplay: applicantHomeRoutesNames.myRenewals.laptopDisplay,
                mobileDisplay: applicantHomeRoutesNames.myRenewals.mobileDisplay,
                navigateTo: applicantHomeRoutesNames.myRenewals.route
              },
              {
                laptopDisplay: applicantHomeRoutesNames.myContractorRegistrations.laptopDisplay,
                mobileDisplay: applicantHomeRoutesNames.myContractorRegistrations.mobileDisplay,
                navigateTo: applicantHomeRoutesNames.myContractorRegistrations.route
              },
              {
                laptopDisplay: applicantHomeRoutesNames.faq.laptopDisplay,
                mobileDisplay: applicantHomeRoutesNames.faq.mobileDisplay,
                navigateTo: applicantHomeRoutesNames.faq.route
              },
              {
                laptopDisplay: applicantHomeRoutesNames.contact.laptopDisplay,
                mobileDisplay: applicantHomeRoutesNames.contact.mobileDisplay,
                navigateTo: applicantHomeRoutesNames.contact.route
              }
            ]
          } else {
            this.applicantHomeDetails.primaryDrawerItems = [
              {
                laptopDisplay: applicantHomeRoutesNames.applyForPermit.laptopDisplay,
                mobileDisplay: applicantHomeRoutesNames.applyForPermit.mobileDisplay,
                navigateTo: applicantHomeRoutesNames.applyForPermit.route
              },
              {
                laptopDisplay: applicantHomeRoutesNames.faq.laptopDisplay,
                mobileDisplay: applicantHomeRoutesNames.faq.mobileDisplay,
                navigateTo: applicantHomeRoutesNames.faq.route
              },
              {
                laptopDisplay: applicantHomeRoutesNames.contact.laptopDisplay,
                mobileDisplay: applicantHomeRoutesNames.contact.mobileDisplay,
                navigateTo: applicantHomeRoutesNames.contact.route
              }
            ]
          }

          setTimeout(() => this.updateMenuScrollButtons());
    });

    if (this.context.user) {
      this.applicantHomeDetails.primaryDrawerItems = [
        {
          laptopDisplay: applicantHomeRoutesNames.applyForPermit.laptopDisplay,
          mobileDisplay: applicantHomeRoutesNames.applyForPermit.mobileDisplay,
          navigateTo: applicantHomeRoutesNames.applyForPermit.route
        },
        {
          laptopDisplay: applicantHomeRoutesNames.myApplications.laptopDisplay,
          mobileDisplay: applicantHomeRoutesNames.myApplications.mobileDisplay,
          navigateTo: applicantHomeRoutesNames.myApplications.route
        },
         // Added by Amisha on 05/21/2026.
        // Reason: keep the logged-in applicant drawer aligned with the new My Renewals Janus page.
        {
          laptopDisplay: applicantHomeRoutesNames.myRenewals.laptopDisplay,
          mobileDisplay: applicantHomeRoutesNames.myRenewals.mobileDisplay,
          navigateTo: applicantHomeRoutesNames.myRenewals.route
        },
        {
          laptopDisplay: applicantHomeRoutesNames.myContractorRegistrations.laptopDisplay,
          mobileDisplay: applicantHomeRoutesNames.myContractorRegistrations.mobileDisplay,
          navigateTo: applicantHomeRoutesNames.myContractorRegistrations.route
        },
        {
          laptopDisplay: applicantHomeRoutesNames.faq.laptopDisplay,
          mobileDisplay: applicantHomeRoutesNames.faq.mobileDisplay,
          navigateTo: applicantHomeRoutesNames.faq.route
        },
        {
          laptopDisplay: applicantHomeRoutesNames.contact.laptopDisplay,
          mobileDisplay: applicantHomeRoutesNames.contact.mobileDisplay,
          navigateTo: applicantHomeRoutesNames.contact.route
        }
      ]
    } else {
      this.applicantHomeDetails.primaryDrawerItems = [
        {
          laptopDisplay: applicantHomeRoutesNames.applyForPermit.laptopDisplay,
          mobileDisplay: applicantHomeRoutesNames.applyForPermit.mobileDisplay,
          navigateTo: applicantHomeRoutesNames.applyForPermit.route
        },
        {
          laptopDisplay: applicantHomeRoutesNames.faq.laptopDisplay,
          mobileDisplay: applicantHomeRoutesNames.faq.mobileDisplay,
          navigateTo: applicantHomeRoutesNames.faq.route
        },
        {
          laptopDisplay: applicantHomeRoutesNames.contact.laptopDisplay,
          mobileDisplay: applicantHomeRoutesNames.contact.mobileDisplay,
          navigateTo: applicantHomeRoutesNames.contact.route
        }
      ]
    }


    setTimeout(() => this.updateMenuScrollButtons());

    // added by priya 21-01-2026
    this.route.parent.paramMap
    .subscribe(params => {
      this.clientId = params.get('clientId');

      this.dataService.getPublicClient(this.clientId)  
      .subscribe(client => {
    
        this.context.client$.emit(client);
      });

      // code added by KRutika
          if (this.clientId) {
            this.checkApplicationAlerts();
          }

    })
    // end by priya 21-01-2026

    WorkflowContextService.isApplicantOpen = false;//added by priya 16-01-2026
    this.context.janusLayout$.emit('applicant')
    // Detect current child route and hide sidebar for routes with data: { hideSidebar: true }
    this.router.events.subscribe((event) => {
      if (!(event instanceof NavigationEnd)) return;

      const childSnapshot = this.route.firstChild?.snapshot;
       // Added by Amisha on 05/25/2026.
      // Reason: renewal forms still return through the shared My Applications route,
      // so redirect that temporary return path back to My Renewals without editing shared AppView logic.
      const shouldReturnToRenewals =
        childSnapshot?.routeConfig?.path === applicantHomeRoutesNames.myApplications.route &&
        childSnapshot?.queryParamMap.get('returnToRegistrations') === '1';

      if (shouldReturnToRenewals) {
        this.router.navigate([applicantHomeRoutesNames.myRenewals.route], {
          relativeTo: this.route,
          queryParams: {
            returnToRegistrations: null,
            returnURL: null
          },
          queryParamsHandling: 'merge',
          replaceUrl: true
        });
        return;
      }

      const shouldHideSidebar = childSnapshot?.data?.['hideSidebar'] === true;
      // added by amisha on 03-12-2025 to manage sidebar visibility based on route data
      this.hideSidebar = shouldHideSidebar;
      //ended by amisha on 03-12-2025

      WorkflowContextService.isApplicantOpen = !shouldHideSidebar;
    });
    // const navState= history.state as any;
    // if (navState?.activeDrawerIndex !== undefined) {
    //   this.selectedDrawerIndex = navState.activeDrawerIndex;
    //   this.isNavigatingFromWorkflow = true;
    // } else {
    //   this.isNavigatingFromWorkflow = false;
    // }

    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        const navState = this.router.getCurrentNavigation()?.extras.state as any;
        if (navState?.activeDrawerIndex !== undefined) {
          this.selectedDrawerIndex = navState.activeDrawerIndex;
          this.isNavigatingFromWorkflow = true;
        } else {
          this.isNavigatingFromWorkflow = false;
        }
      }
    });

    this.getJurisdictionDropdownData()



  }

  ngAfterViewInit(): void {
    setTimeout(() => this.updateMenuScrollButtons());
  }

  scrollMenu(direction: 'left' | 'right'): void {
    const menu = this.primaryDrawerMenu?.nativeElement;

    if (!menu) {
      return;
    }

    const distance = Math.max(menu.clientWidth * 0.75, 160);
    menu.scrollBy({
      left: direction === 'left' ? -distance : distance,
      behavior: 'smooth'
    });
  }

  updateMenuScrollButtons(): void {
    const menu = this.primaryDrawerMenu?.nativeElement;

    if (!menu) {
      this.canScrollMenuLeft = false;
      this.canScrollMenuRight = false;
      return;
    }

    const tolerance = 2;
    this.canScrollMenuLeft = menu.scrollLeft > tolerance;
    this.canScrollMenuRight =
      menu.scrollLeft + menu.clientWidth < menu.scrollWidth - tolerance;
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateMenuScrollButtons();
  }

checkApplicationAlerts() {
  this.dataService.hasMyApplicationAlerts(this.clientId)
    .subscribe({
      next: (res: boolean) => {
        this.hasAlerts = res;
      },
      error: () => {
        this.hasAlerts = false;
      }
    });
}
  onPrimaryDrawerItemClick = (index: number) => {
    this.isNavigatingFromWorkflow = false; // reset first
    this.selectedDrawerIndex = index;
    const route = this.applicantHomeDetails.primaryDrawerItems[index].navigateTo;


  const isMyApplications =
    route === applicantHomeRoutesNames.myApplications.route;

const isComingFromChildRoute =
  this.router.url.split('/').includes('workflow-application');
  if (isMyApplications && isComingFromChildRoute) {
    this.myApplicationsService.triggerRefresh()
  }


    WorkflowContextService.isStepperOpen = false;

    this.router.navigate([route], { relativeTo: this.route });
  };
  //added by priya 16-01-2026
  navigateToPrimaryDrawerItem = (index: number) => {
    const route =
      this.applicantHomeDetails.primaryDrawerItems[index].navigateTo;

    WorkflowContextService.isStepperOpen = false;

    this.router.navigate([route], { relativeTo: this.route });
  };
  //end by priya 16-01-2026

  trackByIndex(index: number, _item: any): number {
    return index;
  }

  // added by Furkan Khan 17-11-2025 -------- //

  // profile button click toggles menu (same signature used by wm-common-button)
  onProfileClick = () => {
    this.applicantHomeDetails.profileMenu.isProfileMenuOpen = !this
      .applicantHomeDetails.profileMenu.isProfileMenuOpen;
  };

  // Added by amisha on 2026-05-11 and updated on 2026-05-22.
  // Reason: keep applicant overlay menus above the chat window across mobile, tablet, and laptop layouts.
  onApplicantProfileMenuOpened(): void {
    this.raiseApplicantOverlayMenu('.applicant-profile-menu-panel');
  }

  onApplicantProfileMenuClosed(): void {
    this.resetApplicantOverlayMenu('.applicant-profile-menu-panel');
  }

  onJurisdictionMenuOpened(): void {
    this.raiseApplicantOverlayMenu('.jurisdiction-menu-panel');
  }

  onJurisdictionMenuClosed(): void {
    this.resetApplicantOverlayMenu('.jurisdiction-menu-panel');
  }

  private raiseApplicantOverlayMenu(panelSelector: string): void {
    const overlayContainer = document.querySelector('.cdk-overlay-container') as HTMLElement | null;

    if (!overlayContainer) {
      return;
    }

    overlayContainer.style.zIndex = '2147483647';
    document.body.appendChild(overlayContainer);

    const overlayPanes = Array.from(
      overlayContainer.querySelectorAll('.cdk-overlay-pane')
    ) as HTMLElement[];

    const targetPane = overlayPanes.find(pane => !!pane.querySelector(panelSelector));

    if (targetPane) {
      targetPane.style.zIndex = '2147483647';
// Added by Priya on 2026-09-21.
// Reason: dynamically center the applicant jurisdiction switcher menu on mobile should open in the correct position and remain dynamically centered/aligned on mobile.
// based on its rendered CDK overlay position instead of using a fixed offset.
  if (window.innerWidth <= 768) {
    setTimeout(() => {
      const rect = targetPane.getBoundingClientRect();

      const viewportCenter = window.innerWidth / 2;
      const menuCenter = rect.left + rect.width / 2;

      const offset = viewportCenter - menuCenter;

      targetPane.style.transform = `translateX(${offset}px)`;
    });
  }
   }
  }

  private resetApplicantOverlayMenu(panelSelector: string): void {
    const overlayContainer = document.querySelector('.cdk-overlay-container') as HTMLElement | null;

    if (!overlayContainer) {
      return;
    }

    const overlayPanes = Array.from(
      overlayContainer.querySelectorAll('.cdk-overlay-pane')
    ) as HTMLElement[];

    overlayPanes.forEach(pane => {
      if (pane.querySelector(panelSelector)) {
        pane.style.zIndex = '';
      }
    });

    const hasElevatedApplicantMenu = overlayPanes.some(pane =>
      this.elevatedApplicantMenuSelectors.some(selector => !!pane.querySelector(selector))
    );

    if (!hasElevatedApplicantMenu) {
      overlayContainer.style.zIndex = '';
    }
  }

  // Added by amisha on 2026-05-11.
  // Reason: keep the old inline-menu close behavior and the new overlay-menu close behavior in sync.
  private closeProfileMenu(): void {
    this.profileMenuTrigger?.closeMenu();
  }

  // menu actions
  goToSettings = () => {
    this.applicantHomeDetails.profileMenu.isProfileMenuOpen = false;
    this.closeProfileMenu();
    this.router.navigate(['settings'], { relativeTo: this.route });
  };
  goToEditProfile = () => {
    this.applicantHomeDetails.profileMenu.isProfileMenuOpen = false;
    this.closeProfileMenu();
    this.router.navigate(['/auth', 'edit-profile'], {
      queryParams: { from: 'applicant' }
    });
  };


  goToChangePassword = () => {
    this.applicantHomeDetails.profileMenu.isProfileMenuOpen = false;
    this.closeProfileMenu();
    this.router.navigate(['/auth', 'change-password'], {
      queryParams: { from: 'applicant' }
    });
  };

  goToChangeEmail = () => {
    this.applicantHomeDetails.profileMenu.isProfileMenuOpen = false;
    this.closeProfileMenu();
    this.router.navigate(['change-email'], { relativeTo: this.route });
  };


  signOutModalData: CommonConfirmationModalData = {
    open: false,
    modalDescp: 'Confirm Sign Out',
    modalSubDescp: 'This will end your session, are you sure you want to sign out?',
    yesNoBtn: true,
    okBtn: false,
    closeBtn: false,
    inTime: 300,
    outTime: 200,
    status: 'default'
  };
  onSignOutClick = () => {

    this.signOutModalData = {
      ...this.signOutModalData,
      open: true
    }
    this.applicantHomeDetails.profileMenu.isProfileMenuOpen = false;
    this.closeProfileMenu();


  }
  handleSignOutModalAccepted() {
  this._securitySvc.logout().subscribe({
    next: () => {
      this.router.navigate([
        '/jurisdiction',
        this.clientId,
        applicantHomeRoutesNames.root,
        applicantHomeRoutesNames.applyForPermit.route
      ]);
    },
    error: () => {
      this.router.navigate([
        '/jurisdiction',
        this.clientId,
        applicantHomeRoutesNames.root,
        applicantHomeRoutesNames.applyForPermit.route
      ]);
    }
  });
}

  handleSignOutModalModalRejected() {

  }

  // Close menu when clicking outside
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const clickedInsideButton = this.applicantHomeDetails.profileMenu.profileBtnWrapRef?.nativeElement.contains(
      event.target
    );
    const clickedInsideMenu = this.applicantHomeDetails.profileMenu.profileMenuRef?.nativeElement?.contains(
      event.target
    );

    if (!clickedInsideButton && !clickedInsideMenu) {
      this.applicantHomeDetails.profileMenu.isProfileMenuOpen = false;
    }
  }

  // Close on Escape key
  // @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey() {
    this.applicantHomeDetails.profileMenu.isProfileMenuOpen = false;
    this.closeProfileMenu();
  }
  // ended by furkan 17-11-2025 ----------- //

  getPrimaryDrawerButtonData = (index: number, template: TemplateRef<any>) => {
    return {
      text: template,
      onClick: () => this.onPrimaryDrawerItemClick(index)
    };
  };
  getHeaderDownArrowButtonData = (template: TemplateRef<any>) => {
    return {
      text: template,
      onClick: () => {
        alert('hello');
      }
    };
  };

  //added by priya 2025-11-12
  toggleStepper(): void {
    this.isStepperOpen = !this.isStepperOpen;
    const el1 = document.querySelector('.header-parent-container') as HTMLElement;
    if (el1) {
      el1.style.zIndex = '1';
    }
  }
  //added by priya 16-01-2026
  get showHamburger(): boolean {
    const urlSegments = this.router.url.split('/');
    const isFromUrl = urlSegments.includes(
      applicantHomeRoutesNames.application.route
    );

    let current = this.route.root;
    while (current.firstChild) {
      current = current.firstChild;
    }
    const isFromRouteData = current.snapshot.data['showHamburger'] || false;

    return isFromUrl || isFromRouteData;
    }

  ngOnDestroy() {
    WorkflowContextService.isStepperOpen = false;
    WorkflowContextService.isApplicantOpen = false;

  }

  get isStepperOpen(): boolean {
    return WorkflowContextService.isStepperOpen;
  }

  set isStepperOpen(value: boolean) {
    WorkflowContextService.isStepperOpen = value;
  }
  //end by priya 16-01-2026


  //end by priya 2025-11-12
  private navigateBasedOnFlow(clientId: string) {

    const url = this.router.url;
  
    const isApplyFlow =
      url.includes(applicantHomeRoutesNames.applyForPermit.route) ||
      url.includes('/application/');

    const isMyRenewalsFlow =
      url.includes(applicantHomeRoutesNames.myRenewals.route);
  
    const isMyApplicationsFlow =
      url.includes(applicantHomeRoutesNames.myApplications.route) ||
      url.includes('workflow-application');

    // Added by Amisha on 09/30/2026.
    // Reason: preserve the selected Janus page when switching jurisdictions from Contractors or FAQ
    // instead of falling back to the legacy GeoPermits jurisdiction page.
    const isMyContractorRegistrationsFlow =
      url.includes(applicantHomeRoutesNames.myContractorRegistrations.route);

    const isFaqFlow =
      url.includes(applicantHomeRoutesNames.faq.route);
  
    const isContactFlow =
      url.includes(applicantHomeRoutesNames.contact.route);
  
    let targetRoute: string[];
  
    if (isApplyFlow) {
  
    targetRoute = [
      '/jurisdiction',
      clientId,
      applicantHomeRoutesNames.root,
      applicantHomeRoutesNames.applyForPermit.route,
    ];
    } 
    else if (isMyRenewalsFlow) {

      targetRoute = [
        '/jurisdiction',
        clientId,
        applicantHomeRoutesNames.root,
        applicantHomeRoutesNames.myRenewals.route
      ];
    }
    else if (isMyApplicationsFlow) {

      targetRoute = [
        '/jurisdiction',
        clientId,
        applicantHomeRoutesNames.root,
        applicantHomeRoutesNames.myApplications.route
      ];
    }
    else if (isMyContractorRegistrationsFlow) {

      targetRoute = [
        '/jurisdiction',
        clientId,
        applicantHomeRoutesNames.root,
        applicantHomeRoutesNames.myContractorRegistrations.route
      ];
    }
    else if (isFaqFlow) {

      targetRoute = [
        '/jurisdiction',
        clientId,
        applicantHomeRoutesNames.root,
        applicantHomeRoutesNames.faq.route
      ];
    }
    else if (isContactFlow) {

      targetRoute = [
        '/jurisdiction',
        clientId,
        applicantHomeRoutesNames.root,
        applicantHomeRoutesNames.contact.route
      ];
    } 
    else {

      targetRoute = ['/jurisdiction', clientId];
    }
  
    const nextUrl = '/' + targetRoute.join('/');
    if (this.router.url === nextUrl) return;
  
    this.router.navigate(targetRoute);
  }
  // added by priya 21-01-2026
  isPrimaryDrawerItemActive(indexOrRoute: number | string): boolean {
    if (this.selectedDrawerIndex !== null && typeof indexOrRoute === 'number') {
      return indexOrRoute === this.selectedDrawerIndex;
    }
    if (typeof indexOrRoute === 'string') {
      const url = this.router.url;

      if (url.includes('/application/') && !url.includes('my-applications')) {
        return indexOrRoute === applicantHomeRoutesNames.applyForPermit.route;

      }

      return url.includes(indexOrRoute);
    }
    return false;
  }

  clientId: string;

  clients: Client[] = [];
  selectedClientName = '';
  jurisdictionsFetchError = ''
  jurisdictionsFetching = false

  getJurisdictionDropdownData = () => {
    this.jurisdictionsFetchError = ''
    this.jurisdictionsFetching = true
    this.dataService.getClientsForUser().
      pipe(finalize(() => {
        this.jurisdictionsFetching = false
      })).subscribe({
        next: (response) => {
          this.clients = response
          this.displayJurisdictions = response;

        },
        error: (_err) => {
          this.jurisdictionsFetchError = 'error occured while fetching jurisdictions'
        }
      });
  };
  onClientMenuItemClick = (client: Client) => {
    return {
      text: client.name,
      onClick: () => {
  
        const clientId = client.id;
        const useNewLayout = !!client.useNewLayout;
  
        this.context.useNewLayout$.emit(useNewLayout);
  
        if (!useNewLayout) {
          this.router.navigate(['/jurisdiction', clientId]);
          return;
        }
  
        this.navigateBasedOnFlow(clientId);
      }
    };
  };
    // end by priya 21-01-2026
  goHome(): void {
    this._router.navigate(['/']);
  }
// added by ketan 05-05-2026
  // --------------------------
    onSignUpClick = ()=>{
 this.router.navigate(['/auth/register']);
  };
  onLoginClick = () => {
  this.router.navigate(['/auth/login'], {
    queryParams: {
      returnURL: this.router.url
    }
  });
};
// --------------------------
  // ended by ketan 05-05-2026

jurisdictionSearchFieldConfig = {
  label: 'Search',
  type: 'text',
  variant: 'outline'
};
jurisdictionSearchQuery: string = '';
displayJurisdictions: any[] = [];

handleJurisdictionSearchChange(value: string): void {
  this.jurisdictionSearchQuery = value;

  const originalList = this.clients;
  if (!value || value.trim() === '') {
    this.displayJurisdictions = [...originalList];
    return;
  }

  const searchText = value.toLowerCase().trim();

  this.displayJurisdictions = originalList.filter(jurisdiction =>
    jurisdiction.name.toLowerCase().includes(searchText)
  );
}

}
